import type { LucideIcon } from "lucide-react"
import { useLayoutEffect, useRef, type ReactNode } from "react"
import { Button } from "@/primitives/button.tsx"
import { cn } from "@/primitives/utils"
import { ButtonLink } from "./button-link.tsx"
import { useLadder } from "./page-chrome.tsx"
import { usePaneShown } from "./pane-switch.tsx"
import { scrollFieldClear, useDockedBar } from "./save-bar-dock.ts"
import { useKeyboardUp, useViewport } from "./viewport.tsx"

/*
 * What a step of a task goes on with, at the foot of its work: a change request's Continue, Write
 * the Spec, Plan the tickets and Create, and the New change request form's Start change request.
 * The page hands it its actions as data, so each layout draws them its own way.
 *
 * On the desk it is the inline footer under the work: a `back` at its start and the `status` at the
 * end beside the primary, or a `cancel` beside the primary, or the status alone before it.
 *
 * On `compact` it is a bar docked at the foot of the screen, as a SaveBar is (`useDockedBar`): on
 * the tab bar, or on the on-screen keyboard while it is up, so the step goes on without scrolling
 * to its end. The status is a line over the buttons, and the primary takes what width the back or
 * cancel leaves it: a back is its arrow alone, and a primary too long for the screen is cut short.
 * Inside a PaneSwitch it shows only while its pane does, so a change request's Chat hides it.
 * Docking on the field being typed in, it scrolls the page until the line being typed on stands
 * clear of it, as a SaveBar does.
 */

/** One of a bar's buttons: what it does, or where it goes, or that it submits its form. */
export type BarAction = {
  label: string
  icon?: LucideIcon
  /** Where its icon stands: before the label (a create's plus, a back arrow) or after (onward). */
  iconAt?: "start" | "end"
  onSelect?: () => void
  /** A link, as Cancel back to a list is. */
  href?: string
  /** The submit button of the form the bar sits in. */
  submit?: boolean
  disabled?: boolean
}

/**
 * One of the bar's actions as a button. On `compact` the primary (`fill`) takes the width the other
 * leaves and cuts its label short rather than run off the screen, and the other is its icon alone
 * when it has one (a back arrow), its label said only to a screen reader.
 */
function ActionButton({
  action,
  variant,
  fill = false,
}: {
  action: BarAction
  variant: "default" | "ghost"
  fill?: boolean
}) {
  const { compact } = useViewport()
  const size = compact ? "default" : "sm"
  const { label, icon: Icon, iconAt = "start" } = action
  const iconOnly = compact && !fill && Icon !== undefined
  const content = iconOnly ? (
    <>
      <Icon aria-hidden />
      <span className="sr-only">{label}</span>
    </>
  ) : (
    <>
      {Icon && iconAt === "start" && <Icon data-icon="inline-start" />}
      {compact ? <span className="truncate">{label}</span> : label}
      {Icon && iconAt === "end" && <Icon data-icon="inline-end" />}
    </>
  )
  if (action.href)
    return (
      <ButtonLink to={action.href} variant={variant} size={iconOnly ? "icon" : size}>
        {content}
      </ButtonLink>
    )
  return (
    <Button
      type={action.submit ? "submit" : "button"}
      variant={variant}
      size={iconOnly ? "icon" : size}
      disabled={action.disabled}
      onClick={action.onSelect}
      className={cn(iconOnly && "size-10", compact && fill && "min-w-0 flex-1 shrink")}
    >
      {content}
    </Button>
  )
}

/**
 * `primary` is the step's action. `back` (a step back, at the desk footer's start) and `cancel`
 * (leaving the form, beside the primary) are its other way; a bar has one or neither. `status` says
 * in a few words what the primary waits on, or what it does.
 */
export function ActionBar({
  primary,
  back,
  cancel,
  status,
}: {
  primary: BarAction
  back?: BarAction
  cancel?: BarAction
  status?: ReactNode
}) {
  const bar = useRef<HTMLDivElement>(null)
  const { compact } = useViewport()
  const shown = usePaneShown()
  const keyboardUp = useKeyboardUp()
  const docked = compact && shown
  const below = useDockedBar(bar, docked)

  // After the frame, so the bar stands where the keyboard and the bars below it put it.
  const { stuck } = useLadder()
  const stuckNow = useRef(stuck)
  stuckNow.current = stuck
  useLayoutEffect(() => {
    if (!docked) return
    const frame = requestAnimationFrame(() =>
      scrollFieldClear(
        bar.current,
        bar.current?.closest("[data-slot=page-scroll]") ?? null,
        stuckNow.current,
      ),
    )
    return () => cancelAnimationFrame(frame)
  }, [docked, keyboardUp, below])

  const other = back ?? cancel
  if (compact)
    return (
      <div
        ref={bar}
        role="region"
        aria-label="Next step"
        data-slot="action-bar"
        hidden={!shown}
        // A fixed bar's bottom margin lifts it off the foot, above the bars docked below it.
        style={below > 0 ? { marginBottom: below } : undefined}
        className={cn(
          "fixed inset-x-0 z-30 flex flex-col gap-1.5 border-t border-border-subtle bg-background px-4 py-2",
          keyboardUp ? "bottom-keyboard" : "bottom-shell",
        )}
      >
        {status && <p className="text-xs text-muted-foreground">{status}</p>}
        <div className="flex items-center gap-2">
          {other && <ActionButton action={other} variant="ghost" />}
          <ActionButton action={primary} variant="default" fill />
        </div>
      </div>
    )
  if (back)
    return (
      <footer className="flex flex-wrap items-center gap-3">
        <ActionButton action={back} variant="ghost" />
        <span className="ml-auto text-ui text-subtle-foreground">{status}</span>
        <ActionButton action={primary} variant="default" />
      </footer>
    )
  if (cancel)
    return (
      <div className="flex items-center justify-end gap-2">
        {status && <p className="text-ui text-subtle-foreground">{status}</p>}
        <ActionButton action={cancel} variant="ghost" />
        <ActionButton action={primary} variant="default" />
      </div>
    )
  return (
    <footer className="flex items-center justify-end gap-3">
      <p className="text-ui text-subtle-foreground">{status}</p>
      <ActionButton action={primary} variant="default" />
    </footer>
  )
}
